// ============================================================
// @yourownmetric/shared — Constants
// Single source of truth for pricing, limits, regions
// ============================================================
export * from './ae-schema.js';
export * from './lens-templates.js';
export * from './dashboard-paths.js';
export * from './org-roles.js';
export const RETENTION_POLICY = {
    free: { defaultDays: 180, maxDays: 180 },
    payg: { defaultDays: 395, maxDays: 1825 },
    complimentary: { defaultDays: 395, maxDays: 1825 },
};
/** The plan's retention policy (unknown/legacy plans resolve like normalizePlan). */
export function retentionPolicy(plan) {
    return RETENTION_POLICY[normalizePlan(plan)];
}
/**
 * Days of data a project keeps: its own setting (or the plan default), capped by the plan.
 * Invalid or missing settings fall back to the plan default.
 */
export function effectiveRetentionDays(plan, configuredDays) {
    const policy = retentionPolicy(plan);
    const days = typeof configuredDays === 'number' && Number.isInteger(configuredDays) && configuredDays > 0
        ? configuredDays
        : policy.defaultDays;
    return Math.min(days, policy.maxDays);
}
export const PLANS = {
    free: {
        name: 'Free',
        slug: 'free',
        description: 'Get started with analytics — no credit card required.',
        features: ['basic_dashboard', 'cookieless_tracking'],
        projects: 1,
        teamMembers: 1,
        dataRetentionDays: RETENTION_POLICY.free.defaultDays,
        freeEventsIncluded: 100_000,
    },
    payg: {
        name: 'Pay as you go',
        slug: 'payg',
        description: 'All features unlocked. Pay only for what you use.',
        features: [
            'basic_dashboard',
            'cookieless_tracking',
            'custom_events',
            'api_access',
            'funnels',
            'embed_dashboards',
            'webhooks',
            'audit_logs',
            'sso',
            'priority_support',
        ],
        projects: Infinity,
        teamMembers: Infinity,
        dataRetentionDays: RETENTION_POLICY.payg.defaultDays,
        freeEventsIncluded: 100_000,
    },
    complimentary: {
        name: 'Complimentary',
        slug: 'complimentary',
        description: 'All features, no charge. Limits are set by the YOM team.',
        features: [
            'basic_dashboard',
            'cookieless_tracking',
            'custom_events',
            'api_access',
            'funnels',
            'embed_dashboards',
            'webhooks',
            'audit_logs',
            'sso',
            'priority_support',
        ],
        projects: Infinity,
        teamMembers: Infinity,
        dataRetentionDays: RETENTION_POLICY.complimentary.defaultDays,
        // The real allowance is the explicit monthly limit set with the subscription.
        freeEventsIncluded: 0,
    },
};
const TENANT_PLANS = ['free', 'payg', 'complimentary'];
/** Map any stored plan string onto a TenantPlan (legacy paid tiers → payg). */
export function normalizePlan(plan) {
    if (plan && TENANT_PLANS.includes(plan)) {
        return plan;
    }
    return plan === 'free' || !plan ? 'free' : 'payg';
}
/** Whether usage is charged (cost estimates, billing warnings, metered usage). */
export function isBillablePlan(plan) {
    return normalizePlan(plan) === 'payg';
}
/**
 * Burst limit: events per minute per organization (all keys share one counter in the
 * ingest limits DO). A key's own rate_limit overrides it. 1,000/min is ~1.4M events a day,
 * far above any site that fits the free allowance, so it only stops runaway loops and floods.
 */
export const DEFAULT_INGEST_EVENTS_PER_MINUTE = 1000;
/** Default grace for exact events over a cap: 10 %. */
export const DEFAULT_EXACT_GRACE_RATIO = 0.1;
/** Usage notifications (percent of the monthly event cap), once per period per project. */
export const USAGE_ALERT_THRESHOLDS = [80, 100];
const DEFAULT_INGEST_LIMIT_POLICY = {
    free: {
        dailyLimit: null,
        weeklyLimit: null,
        monthlyLimit: PLANS.free.freeEventsIncluded,
        // No daily cap: the free allowance is monthly, so a launch-day burst is fine.
        // Abuse protection is the per-minute rate limit (DEFAULT_INGEST_EVENTS_PER_MINUTE).
        absoluteDailyLimit: null,
        // Free "includes 100,000 events every month": past the allowance ingest answers 429
        // (Retry-After = start of next month, UTC) on v1 and v2 alike. Conversion-grade events
        // (purchase, lead, sign-up…) keep the 10 % exact-event grace so no order is lost.
        degradeAfterHardLimit: false,
        degradedLatencyMs: 0,
        degradedAESampleRate: 1,
        exactGraceRatio: DEFAULT_EXACT_GRACE_RATIO,
    },
    payg: {
        dailyLimit: null,
        weeklyLimit: null,
        monthlyLimit: null,
        absoluteDailyLimit: null,
        degradeAfterHardLimit: false,
        degradedLatencyMs: 0,
        degradedAESampleRate: 1,
        exactGraceRatio: DEFAULT_EXACT_GRACE_RATIO,
    },
    // Explicit limits come from tenants.settings.ingestLimits; over the cap → 429 (no overage).
    complimentary: {
        dailyLimit: null,
        weeklyLimit: null,
        monthlyLimit: null,
        absoluteDailyLimit: null,
        degradeAfterHardLimit: false,
        degradedLatencyMs: 0,
        degradedAESampleRate: 1,
        exactGraceRatio: DEFAULT_EXACT_GRACE_RATIO,
    },
};
function normalizeLimit(value, fallback) {
    if (value === null) {
        return null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value > 0 ? Math.floor(value) : null;
    }
    return fallback;
}
function normalizeRate(value, fallback) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return fallback;
    }
    if (value < 0) {
        return 0;
    }
    if (value > 1) {
        return 1;
    }
    return value;
}
/** Resolve effective ingest limits from plan defaults + tenant settings override. */
export function resolveIngestLimitPolicy(plan, tenantSettings) {
    const defaults = DEFAULT_INGEST_LIMIT_POLICY[plan];
    const settings = tenantSettings ?? {};
    return {
        dailyLimit: normalizeLimit(settings.dailyLimit, defaults.dailyLimit),
        weeklyLimit: normalizeLimit(settings.weeklyLimit, defaults.weeklyLimit),
        monthlyLimit: normalizeLimit(settings.monthlyLimit, defaults.monthlyLimit),
        absoluteDailyLimit: normalizeLimit(settings.absoluteDailyLimit, defaults.absoluteDailyLimit),
        degradeAfterHardLimit: typeof settings.degradeAfterHardLimit === 'boolean'
            ? settings.degradeAfterHardLimit
            : defaults.degradeAfterHardLimit,
        degradedLatencyMs: Math.max(0, Math.floor(typeof settings.degradedLatencyMs === 'number'
            ? settings.degradedLatencyMs
            : defaults.degradedLatencyMs)),
        degradedAESampleRate: normalizeRate(settings.degradedAESampleRate, defaults.degradedAESampleRate),
        exactGraceRatio: normalizeRate(settings.exactGraceRatio, defaults.exactGraceRatio ?? DEFAULT_EXACT_GRACE_RATIO),
    };
}
/** Build canonical KV payload for apikey:* entries (shared by API + ingest). */
export function buildAPIKeyCacheRecord(params) {
    return {
        keyHash: params.keyHash,
        tenantId: params.tenantId,
        projectId: params.projectId,
        scopes: params.scopes,
        rateLimit: params.rateLimit,
        plan: params.plan,
        ingestLimitPolicy: resolveIngestLimitPolicy(params.plan, params.tenantSettings),
    };
}
export const CURRENCY_CONFIG = {
    USD: { code: 'USD', symbol: '$', minorUnit: 'cents', locale: 'en-US', decimals: 2 },
    INR: { code: 'INR', symbol: '₹', minorUnit: 'paise', locale: 'en-IN', decimals: 2 },
    CAD: { code: 'CAD', symbol: 'CA$', minorUnit: 'cents', locale: 'en-CA', decimals: 2 },
    EUR: { code: 'EUR', symbol: '€', minorUnit: 'cents', locale: 'de-DE', decimals: 2 },
};
/** Map of ISO-2 country codes to their default billing currency */
const COUNTRY_CURRENCY_MAP = {
    // INR
    IN: 'INR',
    // CAD
    CA: 'CAD',
    // EUR (Eurozone)
    DE: 'EUR',
    FR: 'EUR',
    AT: 'EUR',
    NL: 'EUR',
    BE: 'EUR',
    ES: 'EUR',
    IT: 'EUR',
    PT: 'EUR',
    IE: 'EUR',
    FI: 'EUR',
    GR: 'EUR',
    LU: 'EUR',
    MT: 'EUR',
    CY: 'EUR',
    SK: 'EUR',
    SI: 'EUR',
    EE: 'EUR',
    LV: 'EUR',
    LT: 'EUR',
};
/**
 * Get the default billing currency for a country.
 * Falls back to USD for any country not explicitly mapped.
 */
export function currencyForCountry(countryIso2) {
    return COUNTRY_CURRENCY_MAP[countryIso2.toUpperCase()] ?? 'USD';
}
/** Type guard for SupportedCurrency */
export function isSupportedCurrency(value) {
    return value === 'USD' || value === 'INR' || value === 'CAD' || value === 'EUR';
}
/**
 * Graduated event pricing tiers. Applied in order; each tier's price
 * applies ONLY to events in that range (like income tax brackets).
 *
 * Cost basis per 1K events: ~$0.0037.  Lowest tier is 4x margin.
 */
export const EVENT_TIERS = [
    {
        upTo: 100_000,
        pricePerKUSD: 0,
        pricePerKINR: 0,
        pricePerKCAD: 0,
        pricePerKEUR: 0,
        label: 'Free',
    },
    {
        upTo: 1_000_000,
        pricePerKUSD: 10,
        pricePerKINR: 900,
        pricePerKCAD: 14,
        pricePerKEUR: 9,
        label: 'Tier 1',
    },
    {
        upTo: 10_000_000,
        pricePerKUSD: 6,
        pricePerKINR: 500,
        pricePerKCAD: 8,
        pricePerKEUR: 5,
        label: 'Tier 2',
    },
    {
        upTo: 100_000_000,
        pricePerKUSD: 3,
        pricePerKINR: 250,
        pricePerKCAD: 4,
        pricePerKEUR: 3,
        label: 'Tier 3',
    },
    {
        upTo: Infinity,
        pricePerKUSD: 1.5,
        pricePerKINR: 125,
        pricePerKCAD: 2,
        pricePerKEUR: 1.5,
        label: 'Tier 4',
    },
];
/** Get the per-1K price for an event tier in a given currency (minor units) */
export function tierPricePerK(tier, currency) {
    const key = `pricePerK${currency}`;
    return tier[key];
}
/**
 * AI pricing: $10/mo retainer includes 50 runs.
 * Additional runs use graduated pricing (decreases at higher volumes).
 *
 * Cost estimates:
 *   50 runs  → $10  (retainer only)
 *   100 runs → $20  ($10 + 50×$0.20)
 *   500 runs → $92  ($20 + 400×$0.18)
 *   1000 runs → $167 ($92 + 500×$0.15)
 */
export const AI_PRICING = {
    /** Monthly platform retainer per currency (minor units) */
    retainer: {
        USD: 1000,
        INR: 89900,
        CAD: 1399,
        EUR: 899,
    },
    /** Runs included with the monthly retainer */
    includedRuns: 50,
    /** Graduated run tiers (applied like event tiers — each tier covers its range) */
    runTiers: [
        {
            upTo: 50,
            pricePerRunUSD: 0,
            pricePerRunINR: 0,
            pricePerRunCAD: 0,
            pricePerRunEUR: 0,
            label: 'Included',
        },
        {
            upTo: 100,
            pricePerRunUSD: 20,
            pricePerRunINR: 1800,
            pricePerRunCAD: 27,
            pricePerRunEUR: 17,
            label: 'Tier 1',
        },
        {
            upTo: 500,
            pricePerRunUSD: 18,
            pricePerRunINR: 1600,
            pricePerRunCAD: 25,
            pricePerRunEUR: 15,
            label: 'Tier 2',
        },
        {
            upTo: 1000,
            pricePerRunUSD: 15,
            pricePerRunINR: 1400,
            pricePerRunCAD: 20,
            pricePerRunEUR: 13,
            label: 'Tier 3',
        },
        {
            upTo: Infinity,
            pricePerRunUSD: 12,
            pricePerRunINR: 1100,
            pricePerRunCAD: 16,
            pricePerRunEUR: 10,
            label: 'Tier 4',
        },
    ],
    /** All available agent types */
    agents: [
        'anomaly_detection',
        'daily_summary',
        'weekly_trends',
        'funnel_optimizer',
        'cohort_analyzer',
        'revenue_attribution',
        'executive_summary',
        'nl_query',
    ],
};
/** Get the per-run price for an AI run tier in a given currency (minor units) */
export function runTierPrice(tier, currency) {
    const key = `pricePerRun${currency}`;
    return tier[key];
}
// --- Data Retention & Storage Policy ---
/**
 * Data retention and storage policy.
 *
 * Cloudflare D1 costs: $0.75/GB/month storage, $0.001/M rows read.
 * R2 cold storage: $0.015/GB/month.
 *
 * After the active retention period, data is auto-deleted by the retention cron.
 * Future: archive to R2 cold storage for extended access.
 */
export const STORAGE_POLICY = {
    /** Active data retention in days (queryable, fast) — mirrors PLANS */
    activeRetentionDays: {
        free: RETENTION_POLICY.free.defaultDays,
        payg: RETENTION_POLICY.payg.defaultDays,
    },
    /** Max recommended active DB size per tenant (GB) before perf degrades */
    maxRecommendedSizeGB: {
        free: 0.5,
        payg: 10,
    },
    /** Per GB/month price for extended retention (minor units per currency) */
    extendedRetentionPerGB: {
        USD: 100,
        INR: 9000,
        CAD: 135,
        EUR: 89,
    },
};
// --- Auto-Debit / Billing Thresholds ---
export const BILLING_THRESHOLDS = {
    /** Default auto-charge threshold per currency (minor units) */
    default: {
        USD: 10_000, // $100
        INR: 900_000, // ₹9,000
        CAD: 13_500, // C$135
        EUR: 8_500, // €85
    },
    /** Minimum charge amount per currency (minor units) */
    minimumCharge: {
        USD: 100, // $1
        INR: 5_000, // ₹50
        CAD: 150, // C$1.50
        EUR: 100, // €1
    },
};
// --- Pricing Calculator Utilities ---
/**
 * Calculate the estimated monthly cost for a given event count in any currency.
 * Uses graduated tiering — each tier's rate applies only to events within that bracket.
 *
 * @returns cost in minor units (cents/paise)
 */
export function calculateEventCost(events, currency) {
    let remaining = events;
    let cost = 0;
    let prevUpTo = 0;
    for (const tier of EVENT_TIERS) {
        if (remaining <= 0) {
            break;
        }
        const tierCapacity = tier.upTo - prevUpTo;
        const eventsInTier = Math.min(remaining, tierCapacity);
        cost += (eventsInTier / 1000) * tierPricePerK(tier, currency);
        remaining -= eventsInTier;
        prevUpTo = tier.upTo;
    }
    return Math.round(cost);
}
/**
 * Calculate AI agent run cost using graduated tiers in any currency.
 * First `includedRuns` are free (covered by retainer).
 *
 * @returns cost in minor units (does NOT include the retainer)
 */
export function calculateAiRunCost(runs, currency) {
    let remaining = runs;
    let cost = 0;
    let prevUpTo = 0;
    for (const tier of AI_PRICING.runTiers) {
        if (remaining <= 0) {
            break;
        }
        const tierCapacity = tier.upTo - prevUpTo;
        const runsInTier = Math.min(remaining, tierCapacity);
        cost += runsInTier * runTierPrice(tier, currency);
        remaining -= runsInTier;
        prevUpTo = tier.upTo;
    }
    return Math.round(cost);
}
/**
 * Full monthly cost estimate in any currency (events + optional AI).
 */
export function estimateMonthlyCost(params, currency) {
    const eventsCost = calculateEventCost(params.events, currency);
    const aiRetainer = params.aiEnabled ? AI_PRICING.retainer[currency] : 0;
    const aiRunsCost = params.aiEnabled ? calculateAiRunCost(params.aiRuns ?? 0, currency) : 0;
    return {
        eventsCost,
        aiRetainer,
        aiRunsCost,
        total: eventsCost + aiRetainer + aiRunsCost,
    };
}
export const REGIONS = [
    {
        id: 'us-west',
        name: 'US West',
        d1LocationHint: 'wnam',
        description: 'Western North America',
    },
    {
        id: 'us-east',
        name: 'US East',
        d1LocationHint: 'enam',
        description: 'Eastern North America',
    },
    { id: 'eu-west', name: 'EU West', d1LocationHint: 'weur', description: 'Western Europe' },
    { id: 'eu-east', name: 'EU East', d1LocationHint: 'eeur', description: 'Eastern Europe' },
    { id: 'apac', name: 'Asia Pacific', d1LocationHint: 'apac', description: 'Asia-Pacific' },
];
// --- Rate Limits (per plan tier) ---
export const RATE_LIMITS = {
    /** Ingestion rate limits per API key (events per minute) */
    ingest: {
        free: 100,
        payg: 50_000,
    },
    /** API read rate limits per user (requests per minute) */
    api: {
        free: 60,
        payg: 5_000,
    },
    /** NL query limits per day */
    nlQuery: {
        free: 5,
        payg: 100,
    },
};
// --- SDK Constants ---
export const SDK = {
    DEFAULT_BATCH_SIZE: 20,
    DEFAULT_FLUSH_INTERVAL_MS: 5_000,
    MAX_BATCH_SIZE: 100,
    MAX_EVENT_SIZE_BYTES: 32_768,
    SESSION_TIMEOUT_MS: 30 * 60 * 1_000,
    INGEST_ENDPOINT: '/v1/ingest',
    BEACON_ENDPOINT: '/v1/beacon',
};
// --- API Versioning ---
export const API_VERSION = 'v1';
export const SDK_VERSION = '0.1.0';
// --- Member Role Permissions ---
export const ROLE_PERMISSIONS = {
    owner: ['*'],
    admin: [
        'projects:read',
        'projects:write',
        'api_keys:read',
        'api_keys:write',
        'members:read',
        'members:write',
        'dashboards:read',
        'dashboards:write',
        'agents:read',
        'agents:write',
        'query:read',
        'export:write',
        'settings:read',
        'settings:write',
    ],
    analyst: [
        'projects:read',
        'dashboards:read',
        'dashboards:write',
        'query:read',
        'export:write',
        'agents:read',
    ],
    viewer: ['projects:read', 'dashboards:read', 'query:read'],
};
// --- Billing Provider Configuration ---
/** Countries that route to Razorpay instead of Stripe */
export const RAZORPAY_COUNTRIES = new Set(['IN']);
/**
 * Determine the payment provider for a given country code (ISO 3166-1 alpha-2).
 */
export function providerForCountry(country) {
    return RAZORPAY_COUNTRIES.has(country.toUpperCase()) ? 'razorpay' : 'stripe';
}
/**
 * Whether a plan unlocks paid features (payg and complimentary). For "is usage
 * charged?" use isBillablePlan.
 */
export function isPaidPlan(plan) {
    return plan !== 'free';
}
/**
 * Return the free event allowance for the current plan.
 */
export function freeEventsForPlan(plan) {
    if (plan === 'free' || plan === 'payg' || plan === 'complimentary') {
        return PLANS[plan].freeEventsIncluded;
    }
    // Legacy plans: treat as payg
    return PLANS.payg.freeEventsIncluded;
}
/** Format minor units (cents/paise) to a display string with currency symbol */
export function formatCurrency(minorUnits, currency) {
    const config = CURRENCY_CONFIG[currency];
    const amount = minorUnits / 100;
    return `${config.symbol}${amount.toLocaleString(config.locale, { minimumFractionDigits: config.decimals, maximumFractionDigits: config.decimals })}`;
}
/** Format cents to dollar string (e.g. 1050 → "$10.50") */
export function formatCentsUSD(cents) {
    return formatCurrency(cents, 'USD');
}
/**
 * Resolve a TenantPlan to its PlanDefinition.
 * Any unrecognised value falls back to PAYG.
 */
export function resolvePlan(plan) {
    return PLANS[normalizePlan(plan)];
}
//# sourceMappingURL=index.js.map